import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { SmartLocalStorageService } from './smart-local-storage.service';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import jwt_decode from 'jwt-decode';

declare const gapi: any;
const DM_DEVELOPERS = ['andreas@powerinbox.com'];
const DM_PROJECT_MANAGERS = [];

@Injectable()
export class AuthService {
	returnUrl: any;

	constructor(
		private route: ActivatedRoute,
		private http: HttpClient,
		private localStorage: SmartLocalStorageService,
		private router: Router,
		private zone: NgZone
	) {}

	private handleError(error: HttpErrorResponse) {
		// @ts-ignore
		if (error == 'Server Maintenance') {
			console.error('An error occurred:', error);
			return throwError('Server Maintenance');
		}

		if (error.error instanceof ErrorEvent) {
			// A client-side or network error occurred. Handle it accordingly.
			console.error('An error occurred:', error.error.message);
		} else {
			// The backend returned an unsuccessful response code.
			// The response body may contain clues as to what went wrong,
			console.error(`Backend returned code ${error.status}, ` + `body was: ${error.error}`);
		}
		// return an observable with a user-facing error message
		return throwError('Something bad happened; please try again later.');
	}

	newAPILoginWithEmailAndPassword(email: string, password: string): any {
		const url = `${environment.newApiURL}/login`;
		this.returnUrl = [this.route.snapshot.queryParams['returnUrl'] || '/'];
		const body = `email=${email && email.trim()}&password=${encodeURIComponent(password && password.trim())}`;

		return this.http.post(url, body).pipe(
			map((response: any) => {
				if (response.status && response.token) {
					this.onSuccessUserLoginNewAPI(response.token);
				} else {
					return throwError('Failed to login. Please try again');
				}

				return response;
			}),
			catchError(this.handleError)
		);
	}

	logout() {
		this.router.navigate(['/login']);
		this.localStorage.deleteLocalStorage('piUser');
		this.localStorage.deleteLocalStorage('c-user');

		const auth2 = gapi && gapi.auth2 && gapi.auth2.getAuthInstance();
		if (auth2) {
			auth2.signOut().then(() => {
				console.log('User signed out.');
			});
		}
	}

	getUser(): any {
		const user = this.localStorage.getLocalStorage('c-user');
		return user ? JSON.parse(atob(user)) : user;
	}

	getNewAPILoginToken(originalUser: boolean = false): any {
		const user = this.localStorage.getLocalStorage('c-user');
		if (!user) {
			this.logout();
		}
		const decoded = JSON.parse(atob(user));
		if (originalUser && decoded && typeof decoded['originalUser'] !== 'undefined') {
			return decoded && typeof decoded['originalUser']['jwt'] !== 'undefined' ? decoded['originalUser']['jwt'] : user;
		} else {
			return decoded && typeof decoded['jwt'] !== 'undefined' ? decoded['jwt'] : user;
		}
	}

	getUserObservable(): Observable<string> {
		return this.localStorage.localStorageUser;
	}

	onSuccessUserLoginNewAPI(token: any): void {
		// re-encode to regular base 64
		const decoded = jwt_decode(token);
		decoded['jwt'] = token;
		const encoded = btoa(JSON.stringify(decoded));
		this.localStorage.setLocalStorage('c-user', encoded);

		this.zone.run(() => {
			this.router.navigate(this.returnUrl);
		});
	}

	isWebDeveloper() {
		const user: User = this.getUser();
		if (user) {
			const userEmail = user.workingAs && user.originalUser && user.originalUser['email'] ? user.originalUser['email'] : user.email;
			return [...DM_DEVELOPERS, ...DM_PROJECT_MANAGERS].includes(userEmail);
		} else {
			return false;
		}
	}

	isAdmin() {
		const user: User = this.getUser();
		const isOrigAdmin =
			user && user.originalUser && (user.originalUser['role'] === 'administrator' || user.originalUser['role'] === 'admin');
		const isUserAdmin = user && (user.role === 'administrator' || user.role === 'admin');
		return isOrigAdmin || isUserAdmin;
	}
}

export interface User {
	email: string;
	userId: number;
	uuid: string;
	role: string;
	workingAs?: boolean;
	originalUser?: object;
	jwt?: string;
}
